import React, { Fragment, useState } from 'react'
import { usePutAway } from '../../Context/usePutAway'
import {
  Box,
  Card,
  CardContent,
  Collapse,
  List,
  ListItem,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { createStyles, makeStyles } from '@mui/styles'
import { putAwayActions } from '../../actions/putAwayBusiness'

const useStyles2 = makeStyles({
  card: {
    maxWidth: '100%', // Adjust as needed
    margin: 'auto',
    backgroundColor: '#ffdada', // Light grey background
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)' // Soft shadow for depth
  },
  productName: {
    fontSize: '1rem', // Larger font size for product name
    fontWeight: 'bold', // Bold font weight
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  detailText: {
    fontSize: '0.8rem', // Smaller font size for details
    color: '#5a5a5a' // Slightly muted text color
  },
  excessText: {
    fontSize: '0.8rem', // Smaller font size for details
    color: '#ff6e6e' // Slightly muted text color
  }
})

interface ProductProblemsPutAwayProps {}

export const ProductProblemsPutAway: React.FC<ProductProblemsPutAwayProps> =
  () => {
    const { generateItemList, getElementFromAsnDetails } = putAwayActions()
    const classes = useStyles()

    const { state } = usePutAway()

    const getSKUInfo = (skuId: number) =>
      state.item?.workOrderDetails.filter((el) => el.skuId === skuId)[0]

    return (
      <Fragment>
        <div style={{ padding: '15px 0', textIndent: '15%' }}>
          <Typography variant='button' component={'h2'}>
            Productos con problemas
          </Typography>
          <Typography variant='button' component={'h2'}>
            UBICACIÓN{' '}
            <span style={{ color: 'green' }}>{state.ubication.value}</span>
          </Typography>
        </div>
        <Box
          display='flex'
          flexDirection='column'
          width='100%' // Parent Box width
          bgcolor='lightblue'
          className={classes.container}
        >
          {generateItemList(false)
            .filter((element) => element.withExcess)
            .map(({ id, name, value, batchId }, _, collection) => {
              // obtener elemento completo
              const elementoCompleto = getElementFromAsnDetails(
                id as number,
                batchId
              )

              if (elementoCompleto) {
                const { skuId, realQty, locatedQty } = elementoCompleto
                return (
                  <SKUExcessAlert
                    sku={getSKUInfo(skuId)?.sku || ''}
                    productName={name}
                    excessQuantity={(value as number) - (realQty - locatedQty)}
                    realQuantity={realQty}
                    locatedQuantity={locatedQty}
                    isOpen={collection.length === 1}
                  />
                )
              }

              return null
            })}
        </Box>
      </Fragment>
    )
  }

interface SKUExcessAlertProps {
  sku: string
  productName: string
  excessQuantity: number
  realQuantity: number
  locatedQuantity: number
  isOpen?: boolean
}

export const SKUExcessAlert: React.FC<SKUExcessAlertProps> = ({
  sku,
  productName,
  excessQuantity,
  locatedQuantity,
  realQuantity,
  isOpen
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(
    isOpen ? sku : null
  )
  const [open, setOpen] = useState(isOpen || false)
  const theme = useTheme()
  const isXsScreen = useMediaQuery(theme.breakpoints.down('xs'))

  const classes = useStyles2()

  const handleClick = (sku: string) => {
    setSelectedId(sku)
    setOpen((prevOpen) => !prevOpen)
  }

  return (
    <Box
      padding='10px'
      borderRadius='5px'
      fontFamily='Arial, sans-serif'
      color='#333'
      maxWidth='100%'
      onClick={() => handleClick(sku)}
    >
      <Card className={classes.card}>
        <CardContent>
          <Typography className={classes.productName} gutterBottom>
            {productName}
          </Typography>
          <Box display='flex' justifyContent='space-between'>
            <Typography
              className={classes.detailText}
            >{`SKU: ${sku}`}</Typography>
            <Typography
              className={classes.excessText}
            >{`Sobrantes ${excessQuantity}`}</Typography>
          </Box>
        </CardContent>
      </Card>
      <Collapse in={open && selectedId === sku} timeout='auto' unmountOnExit>
        {isXsScreen ? (
          <MobileFriendlyTable
            real={realQuantity}
            located={locatedQuantity}
            excess={excessQuantity}
          />
        ) : (
          <TableContainer component={Paper}>
            <Table aria-label='simple table'>
              <TableHead>
                <TableRow>
                  <TableCell>Esperados</TableCell>
                  <TableCell>Por Ubicar</TableCell>
                  <TableCell>Ubicado</TableCell>
                  <TableCell>Sobrante</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow key={sku}>
                  <TableCell component='th' scope='row'>
                    {realQuantity}
                  </TableCell>
                  <TableCell>{realQuantity - locatedQuantity}</TableCell>
                  <TableCell>{locatedQuantity}</TableCell>
                  <TableCell>{excessQuantity}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Collapse>
    </Box>
  )
}

interface MobileFriendlyTableProps {
  real: number
  located: number
  excess: number
}

export const MobileFriendlyTable: React.FC<MobileFriendlyTableProps> = ({
  real,
  located,
  excess
}) => {
  return (
    <Paper style={{ margin: '10px', padding: '10px' }}>
      <List component='nav' aria-label='product details'>
        <ListItem>
          <ListItemText primary='Esperados' secondary={real} />
        </ListItem>
        <ListItem>
          <ListItemText primary='Ubicados' secondary={located} />
        </ListItem>
        <ListItem>
          <ListItemText primary='Por Ubicar' secondary={real - located} />
        </ListItem>
        <ListItem>
          <ListItemText primary='Sobrantes' secondary={excess} />
        </ListItem>
      </List>
    </Paper>
  )
}

export const useStyles = makeStyles(() =>
  createStyles({
    container: {
      marginLeft: '5px',
      borderRadius: '5px',
      height: '60vh',
      minHeight: '45vh',
      maxHeight: '80vh',
      width: '95%',
      overflowX: 'hidden',
      overflowY: 'scroll',
      border: '0.5px solid #004BB1',
      background: 'white',
      '&::-webkit-scrollbar': {
        width: '10px',
        backgroundColor: '#F5F5F5'
      },
      '&::-webkit-scrollbar-track': {
        boxShadow: `inset 0 0 6px rgba(0, 0, 0, 0.3)`,
        backgroundColor: '#F5F5F5',
        borderRadius: '5x'
      },
      '&::-webkit-scrollbar-thumb': {
        borderRadius: '5px',
        backgroundImage:
          '-webkit-gradient(linear,left bottom,left top,color-stop(0.44, rgb(122,153,217)),color-stop(0.72, rgb(73,125,189)),color-stop(0.86, rgb(28,58,148)));'
      }
    },
    details: {
      fontSize: 17
    }
  })
)
